import { z } from "zod";
import { FirestoreTimestampSchema } from "./common.schema";

/**
 * Job status enum
 */
export const JobStatusSchema = z.enum(["pending", "completed", "error"]);
export const JobLifecycleStatusSchema = z.enum([
  "pending",
  "completed",
  "error",
  "archived",
  "implemented",
]);
export type JobStatus = z.infer<typeof JobStatusSchema>;

/**
 * Embedded persona reference in job documents
 */
export const PersonaInJobDocSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type PersonaInJobDoc = z.infer<typeof PersonaInJobDocSchema>;

/**
 * Embedded offer reference in job documents
 */
export const OfferInJobDocSchema = z.object({
  id: z.string(),
  offer_name: z.string(),
});
export type OfferInJobDoc = z.infer<typeof OfferInJobDocSchema>;

/**
 * Embedded policy reference in job documents
 */
export const PolicyInJobDocSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type PolicyInJobDoc = z.infer<typeof PolicyInJobDocSchema>;

/**
 * Image dimensions schema
 */
export const ImageDimensionsSchema = z.object({
  width: z.number(),
  height: z.number(),
});
export type ImageDimensions = z.infer<typeof ImageDimensionsSchema>;

/**
 * Page image schema (embedded in job results)
 */
export const PageImageSchema = z.object({
  dimensions: ImageDimensionsSchema,
});
export type PageImage = z.infer<typeof PageImageSchema>;

/**
 * Base job schema - common properties shared by all job types
 */
export const BaseJobSchema = z.object({
  id: z.string(),
  url: z.string(),
  status: JobLifecycleStatusSchema.default("pending"),
  created_at: z.unknown().optional(),
  updated_at: z.unknown().optional(),
  completed_at: z.unknown().nullable().optional(),
  error: z.string().nullable().optional(),
  domain_id: z.string(),
  page_id: z.string(),
  user_id: z.string(),
  // Stamped by core at write time; the audits listener filters on it
  // (see subscribeToJobs in src/store/slices/jobsSlice.ts).
  org_id: z.string().nullable().optional(),
  personas: z.array(PersonaInJobDocSchema).optional(),
  offers: z.array(OfferInJobDocSchema).optional(),
  policies: z.array(PolicyInJobDocSchema).optional(),
  job_type: z.literal("heuristic").default("heuristic"),
  blocking_detected: z.boolean().optional(),
  blocking_type: z
    .enum([
      "none",
      "captcha",
      "access_denied",
      "rate_limit",
      "waf",
      "geo_restriction",
      "ip_block",
      "bot_detection",
      "unknown",
    ])
    .nullable()
    .optional(),
  blocking_details: z.string().nullable().optional(),
  http_status_code: z.number().nullable().optional(),
  proxy_used: z.boolean().optional(),
  // Which conversation turn asked for this run, when the assistant did.
  // Backend-written only; null on a hand-clicked run and on jobs predating it.
  source_thread_id: z.string().nullable().optional(),
  source_message_id: z.string().nullable().optional(),
});
export type BaseJob = z.infer<typeof BaseJobSchema>;

// ----- Heuristic Job Types -----

/**
 * Persona impact in heuristic analysis
 */
const PersonaImpactSchema = z.object({
  name: z.string(),
  impact: z.string(),
  description: z.string(),
});

/**
 * Score detail in heuristic analysis
 */
const ScoreDetailSchema = z.object({
  name: z.string(),
  score: z.number(),
  max_score: z.number(),
  description: z.string().optional(),
});

/**
 * Score category in heuristic analysis
 */
const ScoreCategorySchema = z.object({
  score: z.number(),
  max_score: z.number(),
  details: z.array(ScoreDetailSchema),
});

/**
 * Score breakdown in heuristic analysis
 */
const ScoreSchema = z.object({
  total: z.number(),
  max_possible: z.number(),
  breakdown: z.object({
    impact: ScoreCategorySchema,
    confidence: ScoreCategorySchema,
    effort: ScoreCategorySchema,
  }),
  priority_level: z.string(),
});

/**
 * Implementation suggestion in heuristic analysis
 */
const ImplementationSuggestionSchema = z.object({
  title: z.string(),
  steps: z.array(z.string()),
});

/**
 * Google Analytics evidence in heuristic analysis
 */
const GoogleAnalyticsEvidenceSchema = z.object({
  title: z.string(),
  insight: z.string(),
});

/**
 * Heuristic analysis schema - the structured analysis result
 */
export const HeuristicAnalysisSchema = z.object({
  id: z.string(),
  title: z.string(),
  focus: z.string(),
  hypothesis: z.string(),
  justification: z.string(),
  personas_impacted: z.array(PersonaImpactSchema),
  score: ScoreSchema,
  implementation_suggestion: ImplementationSuggestionSchema,
  potential_risks: z.array(z.string()),
  google_analytics_evidence: z
    .array(GoogleAnalyticsEvidenceSchema)
    .nullable()
    .default(null),
});
export type HeuristicAnalysis = z.infer<typeof HeuristicAnalysisSchema>;

/**
 * AI recommendation in heuristic job result
 */
export const HeuristicAiRecommendationSchema = z.object({
  created_at: z.unknown().optional(),
  text: z.string(),
  valid_json: HeuristicAnalysisSchema.nullable().default(null),
});
export type HeuristicAiRecommendation = z.infer<
  typeof HeuristicAiRecommendationSchema
>;

/**
 * Heuristic job result schema
 */
export const HeuristicJobResultSchema = z.object({
  ai_recommendation: HeuristicAiRecommendationSchema,
  webpage_snapshot: PageImageSchema,
  agentic_provider: z
    .enum(["claude", "langgraph"])
    .nullable()
    .default(null),
  intermediates: z
    .object({
      aggregated_analysis: z.string().nullable().optional(),
      per_persona_findings: z.array(z.object({})).nullable().optional(),
    })
    .nullable()
    .default(null),
  total_execution_time_seconds: z.number().nullable().default(null),
  total_input_tokens: z.number().nullable().default(null),
  total_output_tokens: z.number().nullable().default(null),
  total_tokens: z.number().nullable().default(null),
});
export type HeuristicJobResult = z.infer<typeof HeuristicJobResultSchema>;

/**
 * Heuristic job schema - extends base job with heuristic-specific fields
 */
export const HeuristicJobSchema = BaseJobSchema.extend({
  job_type: z.literal("heuristic").default("heuristic"),
  results: HeuristicJobResultSchema.nullable().optional(),
});
export const HeuristicJobEntitySchema = HeuristicJobSchema.extend({
  created_at: FirestoreTimestampSchema.optional(),
  updated_at: FirestoreTimestampSchema.optional(),
  completed_at: FirestoreTimestampSchema.optional(),
});
export type HeuristicJob = z.infer<typeof HeuristicJobEntitySchema>;

/**
 * Union type for all job types (currently only heuristic)
 */
export const JobSchema = HeuristicJobSchema;
export type Job = z.infer<typeof HeuristicJobEntitySchema>;

// Type aliases for backward compatibility
export type AiRecommendation = HeuristicAiRecommendation;
export type JobResult = HeuristicJobResult;

/**
 * Parse function for HeuristicJob
 */
export const parseHeuristicJob = (data: unknown): HeuristicJob => {
  return HeuristicJobEntitySchema.parse(data);
};

/**
 * Safe parse function for HeuristicJob
 */
export const safeParseHeuristicJob = (data: unknown) => {
  return HeuristicJobEntitySchema.safeParse(data);
};

/**
 * Parse function for Job (union type)
 */
export const parseJob = (data: unknown): Job => {
  return HeuristicJobEntitySchema.parse(data);
};

/**
 * Safe parse function for Job
 */
export const safeParseJob = (data: unknown) => {
  return HeuristicJobEntitySchema.safeParse(data);
};
